import { useChatMutations } from '@/modules/chat/hooks'
import { useAsyncError } from '@/modules/errors'
import {
  ConfirmationFooter,
  useConfirmationCardState,
} from './confirmationCard'

export type CaseFactOperation = 'add' | 'update' | 'remove'

export interface CaseFactPreviewSpec {
  token: string
  operation: CaseFactOperation
  factId: number | null
  text: string | null
  previousText: string | null
  expiresAt: string
}

interface CaseFactPreviewArtifactProps {
  slug: string
  spec: CaseFactPreviewSpec
}

const HEADLINES: Record<CaseFactOperation, string> = {
  add: 'Save a case fact',
  update: 'Update a case fact',
  remove: 'Remove a case fact',
}

export default function CaseFactPreviewArtifact({
  slug,
  spec,
}: CaseFactPreviewArtifactProps) {
  const { sendMessage } = useChatMutations()
  const throwAsyncError = useAsyncError()
  const { state, disabled, setRequested, setCancelledLocally } =
    useConfirmationCardState({
      applyToolName: 'apply_case_fact',
      tokenField: 'token',
      token: spec.token,
      expiresAt: spec.expiresAt,
    })
  const isRemove = spec.operation === 'remove'
  const confirmLabel = isRemove ? 'Remove' : 'Save'
  const struckText = isRemove
    ? (spec.previousText ?? spec.text)
    : spec.previousText

  function applyFact() {
    setRequested(true)
    sendMessage(slug, `${confirmLabel} the case fact.`, {
      kind: 'case_fact',
      token: spec.token,
    }).catch((error: Error) => {
      setRequested(false)
      throwAsyncError(error)
    })
  }

  return (
    <figure className="my-1 w-full overflow-hidden rounded-xl border bg-card shadow-sm">
      <figcaption className="border-b px-4 py-2.5 text-sm font-medium text-foreground">
        {HEADLINES[spec.operation]}
      </figcaption>
      <div className="space-y-1 px-4 py-3 text-sm">
        {struckText && (
          <p className="text-muted-foreground line-through">{struckText}</p>
        )}
        {!isRemove && spec.text && <p>{spec.text}</p>}
      </div>
      <ConfirmationFooter
        expiresAt={spec.expiresAt}
        state={state}
        disabled={disabled}
        appliedMessage="Done. The case facts are updated."
        expiredMessage="Expired. Ask the assistant to propose it again."
        confirmLabel={confirmLabel}
        onApply={applyFact}
        onCancel={() => setCancelledLocally(true)}
      />
    </figure>
  )
}
